import { Component, ElementRef, forwardRef, Inject, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Permissions } from "../../common/constants";
import { Alert, Ticket, User } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { ContextService } from "../../service/context.service";
import { TicketService } from "../../service/ticket.service";
import { CustomPropertyFormComponent } from "../../shared/custom-property/custom-property-form.component";
import { ErrorUtility } from "../../utility/error-utility";

@Component({
    selector: 'ticket-info-dialog',
    template: require('./ticket-info-dialog.component.html')
})
export class TicketInfoDialogComponent {

    @ViewChild('detailsForm') detailsForm: NgForm;

    @ViewChild('noteForm') noteForm: NgForm;

    @ViewChild('noteElement') noteElement: ElementRef;

    @ViewChild('customPropForm') customPropForm: CustomPropertyFormComponent;

    ticket: Ticket;
    isAssegnee: boolean;
    writePermission: boolean;
    error: string;
    timezone: string;
    locale: string;
    savingItem: boolean;
    relatedEvent: Alert;
    isHistoricalEvent: boolean;
    hideEventDetailsButton: boolean;
    newStyleManagerEnabled: boolean;

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => TicketService)) private ticketService: TicketService,
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<TicketInfoDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data
    ) {
        if (data.ticket) {
            this.setTicket(data.ticket);
        } else {
            this.ticketService.getById(data.ticketId).then(ticket => this.setTicket(ticket))
                .catch(err => {
                    this.error = ErrorUtility.getMessage(err);
                    this.dialogRef.close();
                });
        }
        this.timezone = this.contextService.getTimezone();
        this.locale = this.authenticationService.getLocale();
        this.hideEventDetailsButton = data.hideEventDetailsButton;
        this.newStyleManagerEnabled = this.authenticationService.isNewStyleManagerEnabled();
    }

    private setTicket(ticket: Ticket): void {
        this.ticket = ticket;
        if (this.authenticationService.isOrganizationUser()) {
            this.isAssegnee = this.authenticationService.getUser().organizationId == ticket.organizationId;
        } else if (this.authenticationService.isPartnerUser()) {
            this.isAssegnee = this.authenticationService.getUser().partnerId == ticket.partnerId;
        }
        this.writePermission = this.authenticationService.hasPermission(Permissions.WRITE_TICKET);
    }

    close(): void {
        this.dialogRef.close();
    }

    confirmStatusChange(): void {
        this.error = null;
        let body = this.detailsForm.form.getRawValue();
        body.properties = this.customPropForm?.getProperties();
        this.ticketService.patch(this.ticket.id, body).then(() => {
            this.dialogRef.close(true);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    getUserLabel(user: User): string {
        if (!user) {
            return '-';
        }
        if (!user.firstName && !user.lastName) {
            return user.email || '-';
        }
        return [user.firstName, user.lastName].filter(n => n).join(' ');
    }

    saveNote(): void {
        this.savingItem = true;
        const body = this.noteForm.form.getRawValue();
        this.ticketService.saveNote(this.ticket.id, body).then(() => {
            this.ticketService.getById(this.ticket.id).then(ticket => {
                this.ticket = ticket;
                this.savingItem = false;
                setTimeout(() => this.noteElement.nativeElement.scrollIntoView(), 10);
            });
        }).catch(err => {
            this.savingItem = false;
            this.error = ErrorUtility.getMessage(err);
        });
    }

    uploadAttachment(event): void {
        let attachment: File = null;
        const files: FileList = <any>(event.srcElement || event.target).files;
        if (files.length > 0) {
            attachment = files.item(0);
        } else {
            attachment = null;
        }
        if (attachment) {
            this.savingItem = true;
            const data = new FormData();
            data.set('file', attachment, attachment.name);
            this.ticketService.uploadAttachment(this.ticket.id, data).then(() => {
                this.ticketService.getById(this.ticket.id).then(ticket => {
                    this.ticket = ticket;
                    this.savingItem = false;
                })
            }).catch(err => {
                this.savingItem = false;
                this.error = ErrorUtility.getMessage(err);
            });
        }
    }

    isCustomPropertiesFormValid(): boolean {
        if (!this.customPropForm) {
            return true;
        }
        return this.customPropForm.checkForm();
    }

}